/*
 * Copyright (c) 2023 - 2026. Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, useContext, useEffect, useImperativeHandle, useState} from 'react';

import {CompleteInvoiceFilter, CustomerType} from '../../../util/interfaces';

import InvoiceFilter from './invoiceFilter';

import {AppContext} from '../../misc/appContext';

// The order-status filter no longer persists across refreshes — the orders
// list resets all filters on page load, so the panel must start empty too.
const emptyFilters: CompleteInvoiceFilter = {
    status: '',
    order_status: '',
    order_id: null,
    reference: null,
    coc_number: null,
    company: null,
    country: null,
    date_from: null,
    date_to: null,
    invoice_type: null,
    firstname: null,
    lastname: null,
    vat_number: null,
    customer_type: CustomerType.BOTH,
    invoice_num: null,
    ean: null
};

export type InvoiceFilterRef = {
    onReset: VoidFunction;
};

interface Props {
    onSubmit: (filters: CompleteInvoiceFilter) => void;
    onClose: VoidFunction;
    active: boolean;
    filter: Partial<CompleteInvoiceFilter>;
}

function InvoiceFilterController(props: Props, ref: ForwardedRef<InvoiceFilterRef>) {
    const context = useContext(AppContext);
    const {onSubmit, active, onClose, filter} = props;
    const [filters, setFilters] = useState(Object.assign({}, emptyFilters, filter));

    function onChangeFilter(key: keyof CompleteInvoiceFilter) {
        return function (value: string | null) {
            onChangeFilters({[key]: value});
        };
    }

    // Functional update: handlers that set multiple fields in one tick (e.g.
    // the period selector's date_from + date_to) must not clobber each other.
    function onChangeFilters(values: Partial<CompleteInvoiceFilter>) {
        setFilters(current => Object.assign({}, current, values));
    }

    function onResetFilters() {
        // A reset clears EVERY filter. emptyFilters no longer carries any
        // persisted state (the order status used to be restored from
        // localStorage), so it is already the fully-cleared shape.
        setFilters(emptyFilters);
        onSubmit(emptyFilters);
    }

    function onSubmitFilters() {
        onSubmit(filters);
    }

    useImperativeHandle(ref, () => ({
        onReset: onResetFilters
    }));

    useEffect(() => {
        setFilters(Object.assign({}, filters, filter));
    }, [filter]);

    useEffect(() => {
        setFilters(emptyFilters);
    }, [context.accountContext.account]);

    return (
        <InvoiceFilter
            onChangeFilter={onChangeFilter}
            onChangeFilters={onChangeFilters}
            onSubmitFilters={onSubmitFilters}
            onResetFilters={onResetFilters}
            onClose={onClose}
            filters={filters}
            active={active}
        />
    );
}

export default forwardRef<InvoiceFilterRef, Props>(InvoiceFilterController);
